const LEVEL_PRIORITY = {
  debug: 10,
  info: 20,
  warn: 30,
  error: 40,
};

function levelPriority(level) {
  return LEVEL_PRIORITY[String(level || "").toLowerCase()] ?? Number.POSITIVE_INFINITY;
}

const DEFAULT_REMOTE_CONFIG = {
  enabled: false,
  alwaysShipErrors: true,
  remoteDetailLevel: "minimal",
  localConsoleDetailLevel: "minimal",
  levels: ["info", "warn", "error", "debug"],
  categories: ["UI_ACTIVITY", "USER_ERROR", "PERFORMANCE", "NETWORK"],
  users: ["*"],
  modules: ["*"],
  screens: ["*"],
  sourceApps: ["*"],
  maxPayloadBytes: 16 * 1024,
  flushIntervalMs: 5000,
  maxBatchEvents: 25,
  maxBatchBytes: 64 * 1024,
  maxQueueEvents: 200,
  maxEventBytes: 8 * 1024,
  rateLimitPerMinute: 200,
  dedupeWindowMs: 60 * 1000,
  sessionErrorDedupeWindowMs: 15 * 60 * 1000,
  networkSnapshotMinGapMs: 2 * 60 * 1000,
  slowOperationInfoMs: 2000,
  slowOperationWarnMs: 5000,
  rules: [],
};

function isBrowser() {
  return typeof window !== "undefined";
}

function nowIso() {
  return new Date().toISOString();
}

function clampNumber(value, min, max, fallback) {
  const num = Number(value);
  if (!Number.isFinite(num)) return fallback;
  return Math.min(max, Math.max(min, num));
}

function truncateString(value, max = 500) {
  const stringValue = String(value ?? "");
  if (stringValue.length <= max) return stringValue;
  return `${stringValue.slice(0, max)}...[truncated:${stringValue.length - max}]`;
}

function safeJsonParse(value) {
  if (typeof value !== "string") return value;
  try {
    return JSON.parse(value);
  } catch (error) {
    return value;
  }
}

function safeJsonStringify(value) {
  try {
    return JSON.stringify(value);
  } catch (error) {
    return JSON.stringify({ message: "stringify_failed" });
  }
}

function normalizeUserIdentifier(value) {
  const text = String(value || "").trim();
  if (!text) return "";
  return text.includes("@") ? text.toLowerCase() : text;
}

function extractIdentityProviderUser(value) {
  const parsed = safeJsonParse(value);
  const identities = Array.isArray(parsed) ? parsed : [];
  for (const identity of identities) {
    const candidate = normalizeUserIdentifier(
      identity?.userId ||
      identity?.username ||
      identity?.email ||
      identity?.preferred_username ||
      identity?.upn
    );
    if (candidate) return candidate;
  }
  return "";
}

function resolveUserIdentifier(user = {}) {
  const tokenPayload = user?.signInUserSession?.idToken?.payload || {};
  const attributes = user?.attributes || tokenPayload || {};
  return (
    normalizeUserIdentifier(attributes?.email) ||
    normalizeUserIdentifier(tokenPayload?.email) ||
    normalizeUserIdentifier(attributes?.preferred_username) ||
    normalizeUserIdentifier(tokenPayload?.preferred_username) ||
    normalizeUserIdentifier(attributes?.upn) ||
    normalizeUserIdentifier(tokenPayload?.upn) ||
    normalizeUserIdentifier(attributes?.["custom:email"]) ||
    normalizeUserIdentifier(tokenPayload?.["custom:email"]) ||
    extractIdentityProviderUser(attributes?.identities || tokenPayload?.identities) ||
    normalizeUserIdentifier(user?.email) ||
    normalizeUserIdentifier(attributes?.username) ||
    normalizeUserIdentifier(tokenPayload?.username) ||
    normalizeUserIdentifier(attributes?.["cognito:username"]) ||
    normalizeUserIdentifier(tokenPayload?.["cognito:username"]) ||
    normalizeUserIdentifier(user?.username) ||
    normalizeUserIdentifier(attributes?.sub) ||
    normalizeUserIdentifier(tokenPayload?.sub) ||
    normalizeUserIdentifier(user?.sub) ||
    null
  );
}

const SENSITIVE_KEY_PATTERN =
  /token|authorization|cookie|secret|password|passwd|session|jwt|apikey|api_key|accesskey|access_key|refresh|credential|set-cookie/i;

function maskSensitiveText(value) {
  const text = String(value ?? "");
  if (!text) return text;
  if (text.length <= 8) return "[masked]";
  return `${text.slice(0, 2)}***${text.slice(-2)}`;
}

function hashSensitiveValue(value) {
  return `[hashed:${hashString(typeof value === "string" ? value : safeJsonStringify(value))}]`;
}

function sanitizeValue(value, options = {}, depth = 0, keyName = "") {
  const detailLevel = options.detailLevel || "minimal";
  const maxDepth = options.maxDepth ?? (detailLevel === "full" ? 6 : detailLevel === "enhanced" ? 5 : 4);
  const maxArray = options.maxArray ?? (detailLevel === "full" ? 50 : detailLevel === "enhanced" ? 30 : 20);
  const maxEntries = options.maxEntries ?? (detailLevel === "full" ? 50 : detailLevel === "enhanced" ? 35 : 25);
  const maxString = options.maxString ?? (detailLevel === "full" ? 4000 : detailLevel === "enhanced" ? 2000 : 1200);
  if (depth > maxDepth) return "[depth-truncated]";
  if (value === null || value === undefined) return value;
  if (keyName && SENSITIVE_KEY_PATTERN.test(String(keyName))) {
    return typeof value === "string" ? maskSensitiveText(value) : hashSensitiveValue(value);
  }
  if (value instanceof Error) {
    return {
      name: value.name,
      message: truncateString(value.message, Math.min(maxString, 1200)),
      stack:
        detailLevel === "full"
          ? truncateString(value.stack || "", Math.min(maxString, 3000))
          : truncateString(value.stack || "", 1200),
    };
  }
  if (typeof value === "string") return truncateString(value, maxString);
  if (typeof value === "number" || typeof value === "boolean") return value;
  if (typeof value === "function") return `[function:${value.name || "anonymous"}]`;
  if (Array.isArray(value)) {
    return value.slice(0, maxArray).map((item) => sanitizeValue(item, options, depth + 1));
  }
  if (typeof value === "object") {
    const entries = Object.entries(value).slice(0, maxEntries);
    return Object.fromEntries(
      entries.map(([key, innerValue]) => [key, sanitizeValue(innerValue, options, depth + 1, key)])
    );
  }
  return truncateString(value, Math.min(maxString, 500));
}

function maskIdentifier(value) {
  const text = String(value || "").trim();
  if (!text) return null;
  if (text.includes("@")) {
    const [localPart, domainPart] = text.split("@");
    const maskedLocal =
      localPart.length <= 2 ? `${localPart[0] || "*"}*` : `${localPart.slice(0, 2)}***`;
    return `${maskedLocal}@${domainPart || ""}`;
  }
  if (text.length <= 6) return `${text.slice(0, 1)}***`;
  return `${text.slice(0, 3)}***${text.slice(-2)}`;
}

function hashString(input) {
  const value = String(input || "");
  let hash = 5381;
  for (let index = 0; index < value.length; index += 1) {
    hash = (hash * 33) ^ value.charCodeAt(index);
  }
  return `h${(hash >>> 0).toString(16)}`;
}

function newId() {
  if (typeof crypto !== "undefined" && crypto.randomUUID) {
    return crypto.randomUUID();
  }
  return `evt-${Date.now()}-${Math.random().toString(16).slice(2, 10)}`;
}

function getSessionStorageSafe(key) {
  if (!isBrowser()) return null;
  try {
    return window.sessionStorage.getItem(key);
  } catch (error) {
    return null;
  }
}

function setSessionStorageSafe(key, value) {
  if (!isBrowser()) return;
  try {
    window.sessionStorage.setItem(key, value);
  } catch (error) {
    // Ignore storage failures.
  }
}

function normalizeList(value, fallback = ["*"]) {
  if (!value) return fallback;
  if (value === "*") return ["*"];
  if (Array.isArray(value)) return value.length ? value.map(String) : fallback;
  return [String(value)];
}

function buildPathContext(pathname) {
  const fullPath = String(pathname || (isBrowser() ? window.location.pathname : "/") || "/");
  const pathOnly = fullPath.split("?")[0].split("#")[0];
  const segments = pathOnly.split("/").filter(Boolean);
  const moduleName = segments[1] || segments[0] || "root";
  return {
    module: moduleName,
    screen: pathOnly || "/",
  };
}

function inferCategory(level, eventType, meta) {
  if (meta?.category) return meta.category;
  if (meta?.authSessionError) return "USER_ERROR";
  if (eventType === "performance" || meta?.durationMs !== undefined) return "PERFORMANCE";
  if (eventType === "network-snapshot") return "NETWORK";
  if (level === "error" || level === "warn") return "USER_ERROR";
  return "UI_ACTIVITY";
}

function isNetworkErrorMessage(message) {
  const text = String(message || "");
  return text.includes("xhr-http-handler") && /\bnetwork\s*error\b/i.test(text);
}

function normalizeForIssueKey(value) {
  return String(value || "")
    .replace(/\b[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}\b/gi, "<uuid>")
    .replace(/\b[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}\b/gi, "<email>")
    .replace(/\b\d{4,}\b/g, "<n>")
    .replace(/\s+/g, " ")
    .trim()
    .toLowerCase();
}

function isAuthSessionMessage(message) {
  const normalized = normalizeForIssueKey(message);
  return [
    "jwt expired",
    "token expired",
    "session expired",
    "no current user",
    "the security token included in the request is invalid",
    "not authorized",
    "unauthorized",
    "authentication error",
    "invalid access token",
    "failed to fetch user",
    "missing credentials",
  ].some((pattern) => normalized.includes(pattern));
}

function toExpiryIso(baseValue, durationValue, fallbackHours = 1) {
  const fetchedAt = new Date();
  const enabledAtBase = baseValue ? new Date(baseValue) : fetchedAt;
  const safeEnabledAt = Number.isFinite(enabledAtBase.getTime()) ? enabledAtBase : fetchedAt;
  const durationHours = clampNumber(durationValue ?? fallbackHours, 1, 24, fallbackHours);
  return new Date(safeEnabledAt.getTime() + durationHours * 60 * 60 * 1000).toISOString();
}

function normalizeRule(rule = {}) {
  const parsed = safeJsonParse(rule) || {};
  const expiresAt = parsed.expiresAt || toExpiryIso(parsed.enabledAt, parsed.durationHours, 1);
  return {
    id: parsed.id || null,
    enabled: parsed.enabled !== false,
    expiresAt,
    levels: normalizeList(parsed.levels, DEFAULT_REMOTE_CONFIG.levels),
    categories: normalizeList(parsed.categories, DEFAULT_REMOTE_CONFIG.categories),
    users: normalizeList(parsed.users || parsed.userIds, DEFAULT_REMOTE_CONFIG.users),
    modules: normalizeList(parsed.modules, DEFAULT_REMOTE_CONFIG.modules),
    screens: normalizeList(parsed.screens, DEFAULT_REMOTE_CONFIG.screens),
    sourceApps: normalizeList(parsed.sourceApps, DEFAULT_REMOTE_CONFIG.sourceApps),
    remoteDetailLevel: ["minimal", "enhanced", "full"].includes(parsed.remoteDetailLevel)
      ? parsed.remoteDetailLevel
      : DEFAULT_REMOTE_CONFIG.remoteDetailLevel,
    localConsoleDetailLevel: ["minimal", "enhanced", "full"].includes(parsed.localConsoleDetailLevel)
      ? parsed.localConsoleDetailLevel
      : DEFAULT_REMOTE_CONFIG.localConsoleDetailLevel,
    maxPayloadBytes: clampNumber(
      parsed.maxPayloadBytes,
      2 * 1024,
      64 * 1024,
      DEFAULT_REMOTE_CONFIG.maxPayloadBytes
    ),
  };
}

function buildBrowserContext() {
  if (!isBrowser()) return {};
  return {
    userAgent: truncateString(window.navigator?.userAgent || "", 500),
    language: window.navigator?.language || null,
    platform: window.navigator?.platform || null,
    viewport: `${window.innerWidth || 0}x${window.innerHeight || 0}`,
    deviceMemoryGb: window.navigator?.deviceMemory ?? null,
    hardwareConcurrency: window.navigator?.hardwareConcurrency ?? null,
  };
}

function readConnectionContext() {
  if (!isBrowser()) return {};
  const connection =
    window.navigator?.connection ||
    window.navigator?.mozConnection ||
    window.navigator?.webkitConnection;

  return {
    online: typeof window.navigator?.onLine === "boolean" ? window.navigator.onLine : null,
    effectiveType: connection?.effectiveType || null,
    downlinkMbps:
      typeof connection?.downlink === "number" ? Number(connection.downlink) : null,
    rttMs: typeof connection?.rtt === "number" ? Number(connection.rtt) : null,
    saveData: typeof connection?.saveData === "boolean" ? connection.saveData : null,
  };
}

function classifyNetworkQuality(snapshot = {}) {
  const reasons = [];
  if (snapshot.online === false) {
    return { status: "offline", reasons: ["browser_offline"] };
  }
  if (snapshot.saveData) reasons.push("save_data");
  if (typeof snapshot.pingMs === "number" && snapshot.pingMs >= 1200) reasons.push("very_high_ping");
  else if (typeof snapshot.pingMs === "number" && snapshot.pingMs >= 600) reasons.push("high_ping");
  if (typeof snapshot.rttMs === "number" && snapshot.rttMs >= 600) reasons.push("high_rtt");
  if (typeof snapshot.downlinkMbps === "number" && snapshot.downlinkMbps > 0 && snapshot.downlinkMbps < 1) reasons.push("low_downlink");
  else if (typeof snapshot.downlinkMbps === "number" && snapshot.downlinkMbps > 0 && snapshot.downlinkMbps < 3) reasons.push("limited_downlink");
  if (["slow-2g", "2g"].includes(snapshot.effectiveType)) reasons.push("2g_class");
  if (snapshot.effectiveType === "3g") reasons.push("3g_class");

  if (reasons.includes("very_high_ping") || reasons.includes("low_downlink") || reasons.includes("2g_class")) {
    return { status: "poor", reasons };
  }
  if (
    reasons.includes("high_ping") ||
    reasons.includes("high_rtt") ||
    reasons.includes("limited_downlink") ||
    reasons.includes("3g_class") ||
    reasons.includes("save_data")
  ) {
    return { status: "degraded", reasons };
  }
  return { status: "good", reasons };
}

function describeInteractionTarget(target) {
  if (!target || typeof target.closest !== "function") return null;
  const element =
    target.closest("[data-trace-label]") ||
    target.closest("button, a, input, select, textarea, [role='button'], [type='submit']");
  if (!element) return null;
  return {
    tagName: String(element.tagName || "").toLowerCase(),
    id: element.id || null,
    name: element.getAttribute?.("name") || null,
    role: element.getAttribute?.("role") || null,
    type: element.getAttribute?.("type") || null,
    label: truncateString(
      element.getAttribute?.("data-trace-label") ||
        element.getAttribute?.("aria-label") ||
        element.getAttribute?.("title") ||
        element.innerText ||
        element.textContent ||
        element.value ||
        "",
      120
    ),
  };
}

export class UserTracerCore {
  constructor({ sourceApp, instanceBackend, instanceRegion, fetchRemoteConfig, shipBatch }) {
    this.sourceApp = sourceApp;
    this.instanceBackend = instanceBackend;
    this.instanceRegion = instanceRegion;
    this.fetchRemoteConfig = fetchRemoteConfig;
    this.shipBatch = shipBatch;
    this.queue = [];
    this.flushTimer = null;
    this.refreshTimer = null;
    this.dedupeMap = new Map();
    this.originalConsole = null;
    this.consoleInstalled = false;
    this.globalListenersInstalled = false;
    this.runtimeConfig = { ...DEFAULT_REMOTE_CONFIG, localhost: false };
    this.userContext = {};
    this.flushInFlight = false;
    this.minuteWindow = { bucket: 0, count: 0 };
    this.droppedCounters = {};
    this.sessionId = getSessionStorageSafe("mea.userTracer.sessionId") || newId();
    this.lastNetworkSnapshot = null;
    this.lastNetworkFingerprint = "";
    this.lastNetworkProbeAt = 0;
    this.networkProbeInFlight = null;
    setSessionStorageSafe("mea.userTracer.sessionId", this.sessionId);
  }

  bootstrap() {
    if (!isBrowser()) return;
    this.installConsoleBridge();
    window.__MEA_USER_TRACER__ = this;
    window.__MEA_USER_TRACER_ORIGINAL_CONSOLE__ = this.originalConsole;
    this.installGlobalListeners();
    this.refreshConfig();
    this.captureNetworkSnapshot("bootstrap", { silent: true });
    if (!this.refreshTimer) {
      this.refreshTimer = window.setInterval(() => {
        this.refreshConfig();
      }, 60 * 1000);
    }
  }

  isLocalhost() {
    if (!isBrowser()) return false;
    const host = window.location.hostname || "";
    return host === "localhost" || host === "127.0.0.1" || host === "::1";
  }

  normalizeRemoteConfig(rawConfig) {
    const parsed = safeJsonParse(rawConfig) || {};
    const expiresAt = parsed.expiresAt || toExpiryIso(parsed.enabledAt, parsed.durationHours ?? parsed.ttlHours, 1);

    return {
      enabled: Boolean(parsed.enabled),
      expiresAt,
      remoteDetailLevel:
        ["minimal", "enhanced", "full"].includes(parsed.remoteDetailLevel)
          ? parsed.remoteDetailLevel
          : DEFAULT_REMOTE_CONFIG.remoteDetailLevel,
      localConsoleDetailLevel:
        ["minimal", "enhanced", "full"].includes(parsed.localConsoleDetailLevel)
          ? parsed.localConsoleDetailLevel
          : DEFAULT_REMOTE_CONFIG.localConsoleDetailLevel,
      levels: normalizeList(parsed.levels, DEFAULT_REMOTE_CONFIG.levels),
      categories: normalizeList(parsed.categories, DEFAULT_REMOTE_CONFIG.categories),
      users: normalizeList(parsed.users || parsed.userIds, DEFAULT_REMOTE_CONFIG.users),
      modules: normalizeList(parsed.modules, DEFAULT_REMOTE_CONFIG.modules),
      screens: normalizeList(parsed.screens, DEFAULT_REMOTE_CONFIG.screens),
      sourceApps: normalizeList(parsed.sourceApps, DEFAULT_REMOTE_CONFIG.sourceApps),
      maxPayloadBytes: clampNumber(
        parsed.maxPayloadBytes,
        2 * 1024,
        64 * 1024,
        DEFAULT_REMOTE_CONFIG.maxPayloadBytes
      ),
      flushIntervalMs: clampNumber(parsed.flushIntervalMs, 1000, 15000, DEFAULT_REMOTE_CONFIG.flushIntervalMs),
      maxBatchEvents: clampNumber(parsed.maxBatchEvents, 5, 50, DEFAULT_REMOTE_CONFIG.maxBatchEvents),
      maxBatchBytes: clampNumber(parsed.maxBatchBytes, 8 * 1024, 256 * 1024, DEFAULT_REMOTE_CONFIG.maxBatchBytes),
      maxQueueEvents: clampNumber(parsed.maxQueueEvents, 25, 500, DEFAULT_REMOTE_CONFIG.maxQueueEvents),
      maxEventBytes: clampNumber(parsed.maxEventBytes, 2 * 1024, 16 * 1024, DEFAULT_REMOTE_CONFIG.maxEventBytes),
      rateLimitPerMinute: clampNumber(parsed.rateLimitPerMinute, 20, 1000, DEFAULT_REMOTE_CONFIG.rateLimitPerMinute),
      dedupeWindowMs: clampNumber(parsed.dedupeWindowMs, 5 * 1000, 10 * 60 * 1000, DEFAULT_REMOTE_CONFIG.dedupeWindowMs),
      sessionErrorDedupeWindowMs: clampNumber(
        parsed.sessionErrorDedupeWindowMs,
        60 * 1000,
        60 * 60 * 1000,
        DEFAULT_REMOTE_CONFIG.sessionErrorDedupeWindowMs
      ),
      networkSnapshotMinGapMs: clampNumber(
        parsed.networkSnapshotMinGapMs,
        30 * 1000,
        15 * 60 * 1000,
        DEFAULT_REMOTE_CONFIG.networkSnapshotMinGapMs
      ),
      slowOperationInfoMs: clampNumber(
        parsed.slowOperationInfoMs,
        250,
        30000,
        DEFAULT_REMOTE_CONFIG.slowOperationInfoMs
      ),
      slowOperationWarnMs: clampNumber(
        parsed.slowOperationWarnMs,
        500,
        60000,
        DEFAULT_REMOTE_CONFIG.slowOperationWarnMs
      ),
      alwaysShipErrors: parsed.alwaysShipErrors !== false,
      rules: Array.isArray(parsed.rules) ? parsed.rules.map(normalizeRule) : [],
      localhost: false,
    };
  }

  async refreshConfig() {
    if (this.isLocalhost()) {
      this.runtimeConfig = {
        ...DEFAULT_REMOTE_CONFIG,
        enabled: true,
        alwaysShipErrors: true,
        expiresAt: new Date(Date.now() + 24 * 60 * 60 * 1000).toISOString(),
        levels: ["debug", "info", "warn", "error"],
        rules: [normalizeRule({ levels: ["debug", "info", "warn", "error"] })],
        localhost: true,
      };
      return;
    }

    try {
      const remoteConfig = await this.fetchRemoteConfig();
      this.runtimeConfig = this.normalizeRemoteConfig(remoteConfig);
    } catch (error) {
      this.runtimeConfig = { ...DEFAULT_REMOTE_CONFIG, enabled: false, localhost: false };
    }
  }

  setUserContext(user) {
    const attributes = user?.attributes || user?.signInUserSession?.idToken?.payload || {};
    const resolvedUserId = resolveUserIdentifier(user);
    this.userContext = {
      userId: resolvedUserId,
      username:
        normalizeUserIdentifier(user?.username) ||
        normalizeUserIdentifier(attributes?.email) ||
        normalizeUserIdentifier(attributes?.preferred_username) ||
        normalizeUserIdentifier(attributes?.upn) ||
        normalizeUserIdentifier(attributes?.sub) ||
        null,
      accessToken:
        user?.signInUserSession?.idToken?.jwtToken ||
        user?.signInUserSession?.accessToken?.jwtToken ||
        null,
    };
  }

  installConsoleBridge() {
    if (this.consoleInstalled || typeof console === "undefined") return;
    this.consoleInstalled = true;
    this.originalConsole = {
      debug: console.debug.bind(console),
      info: console.info.bind(console),
      log: console.log.bind(console),
      warn: console.warn.bind(console),
      error: console.error.bind(console),
    };

    const wrap = (level, original) => {
      return (...args) => {
        if (this.isLocalhost()) {
          original(...args);
          return;
        }

        const message = args
          .map((arg) => {
            if (typeof arg === "string") return arg;
            if (arg instanceof Error) return arg.message || String(arg);
            return safeJsonStringify(sanitizeValue(arg, { detailLevel: "minimal" }));
          })
          .join(" ");
        const traceLevel = isNetworkErrorMessage(message) ? "debug" : level;
        const eventType = traceLevel === "error" || traceLevel === "warn" ? "client-log-error" : "client-log";
        this.trace(
          traceLevel,
          eventType,
          message,
          {
            category: traceLevel === "error" || traceLevel === "warn" ? "USER_ERROR" : "UI_ACTIVITY",
            data: { args },
            localConsoleArgs: args,
          }
        );
      };
    };

    console.debug = wrap("debug", this.originalConsole.debug);
    console.info = wrap("info", this.originalConsole.info);
    console.log = wrap("info", this.originalConsole.log);
    console.warn = wrap("warn", this.originalConsole.warn);
    console.error = wrap("error", this.originalConsole.error);
  }

  installGlobalListeners() {
    if (this.globalListenersInstalled || !isBrowser()) return;
    this.globalListenersInstalled = true;

    window.addEventListener("error", (event) => {
      this.captureNetworkSnapshot("window-error", { silent: true, force: true });
      this.trace("error", "window-error", event.message || "Unhandled window error", {
        category: "USER_ERROR",
        data: {
          filename: event.filename,
          lineno: event.lineno,
          colno: event.colno,
          error: event.error,
        },
      });
    });

    window.addEventListener("unhandledrejection", (event) => {
      this.captureNetworkSnapshot("unhandled-rejection", { silent: true, force: true });
      this.trace("error", "unhandled-rejection", "Unhandled promise rejection", {
        category: "USER_ERROR",
        data: event.reason,
      });
    });

    window.addEventListener("online", () => {
      this.captureNetworkSnapshot("online", { silent: false, force: true });
      this.trace("info", "network-status", "Browser reported online", {
        category: "NETWORK",
      });
    });

    window.addEventListener("offline", () => {
      this.captureNetworkSnapshot("offline", { silent: false, force: true });
      this.trace("warn", "network-status", "Browser reported offline", {
        category: "NETWORK",
      });
    });

    window.addEventListener(
      "load",
      () => {
        const navigationEntry =
          typeof performance !== "undefined" && typeof performance.getEntriesByType === "function"
            ? performance.getEntriesByType("navigation")?.[0]
            : null;
        this.captureNetworkSnapshot("page-load", { silent: true });
        this.trace("info", "page-load", "Page load completed", {
          category: "PERFORMANCE",
          module: "app",
          screen: isBrowser() ? window.location.pathname : "/",
          durationMs: navigationEntry?.loadEventEnd
            ? Math.round(navigationEntry.loadEventEnd)
            : undefined,
          data: {
            navigation: navigationEntry || null,
          },
        });
      },
      { once: true }
    );

    const trackInteraction = (interactionType, baseLevel = "debug") => (event) => {
      const targetInfo = describeInteractionTarget(event.target);
      const pathContext = buildPathContext(window.location.pathname);
      if (targetInfo) {
        this.trace(baseLevel, interactionType, `User ${interactionType.replace(/^ui-/, "").replace(/-/g, " ")}`, {
          category: "UI_ACTIVITY",
          module: pathContext.module,
          screen: window.location.pathname,
          data: { target: targetInfo },
        });
      }
      this.measureInteraction(interactionType, event, targetInfo, pathContext);
    };

    [
      ["click", "ui-click", "debug"],
      ["submit", "ui-submit", "info"],
      ["change", "ui-change", "debug"],
      ["input", "ui-input", "debug"],
      ["focus", "ui-focus", "debug"],
      ["blur", "ui-blur", "debug"],
      ["keydown", "ui-keydown", "debug"],
      ["drop", "ui-drop", "info"],
    ].forEach(([domEvent, interactionType, level]) => {
      document.addEventListener(domEvent, trackInteraction(interactionType, level), true);
    });
  }

  measureInteraction(interactionType, event, targetInfo, pathContext) {
    if (!isBrowser()) return;
    const nowMs = typeof performance !== "undefined" ? performance.now() : Date.now();
    const eventTs = typeof event?.timeStamp === "number" ? event.timeStamp : nowMs;
    const dispatchLagMs = Math.max(0, Math.round(nowMs - eventTs));
    const startedAt = typeof performance !== "undefined" ? performance.now() : Date.now();

    const finalize = () => {
      const endedAt = typeof performance !== "undefined" ? performance.now() : Date.now();
      const durationMs = Math.max(0, Math.round(endedAt - startedAt));
      const totalLatencyMs = Math.max(dispatchLagMs, Math.round(dispatchLagMs + durationMs));
      const level =
        totalLatencyMs >= this.runtimeConfig.slowOperationWarnMs
          ? "warn"
          : totalLatencyMs >= this.runtimeConfig.slowOperationInfoMs
            ? "info"
            : "debug";
      this.trace(level, "performance", `${interactionType} settled`, {
        category: "PERFORMANCE",
        module: pathContext.module,
        screen: pathContext.screen,
        durationMs: totalLatencyMs,
        impactKind: "user-blocking",
        impactReason: "Triggered from a user interaction and measured until the UI settled.",
        data: {
          interactionType,
          dispatchLagMs,
          settleMs: durationMs,
          target: targetInfo,
        },
      });
    };

    if (typeof requestAnimationFrame === "function") {
      requestAnimationFrame(() => requestAnimationFrame(finalize));
      return;
    }
    window.setTimeout(finalize, 0);
  }

  isExpired() {
    if (!this.runtimeConfig?.expiresAt) return true;
    return new Date(this.runtimeConfig.expiresAt).getTime() <= Date.now();
  }

  isRuleExpired(rule) {
    if (!rule?.expiresAt) return false;
    const expiresAt = new Date(rule.expiresAt).getTime();
    return Number.isFinite(expiresAt) && expiresAt <= Date.now();
  }

  matchesFilter(list, value) {
    if (!Array.isArray(list) || list.includes("*")) return true;
    const normalizedValue = String(value || "");
    return list.map(String).includes(normalizedValue);
  }

  matchesLevelFilter(list, level) {
    if (!Array.isArray(list) || list.includes("*")) return true;
    const thresholds = list
      .map((item) => String(item || "").toLowerCase())
      .map(levelPriority)
      .filter(Number.isFinite);
    if (!thresholds.length) return true;
    return levelPriority(level) >= Math.min(...thresholds);
  }

  shouldTrace(level, meta = {}) {
    if (this.isLocalhost()) return false;
    const authSessionError = Boolean(meta?.authSessionError);
    if ((level === "error" && this.runtimeConfig?.alwaysShipErrors !== false) || authSessionError) {
      return true;
    }

    const { module, screen } = buildPathContext(meta.screen);
    const category = inferCategory(level, meta.eventType, meta);
    const userId = meta.userId || this.userContext.userId || "anonymous";
    const activeRules = (this.runtimeConfig.rules || []).filter(
      (rule) => rule.enabled !== false && !this.isRuleExpired(rule)
    );

    if (activeRules.length) {
      return activeRules.some((rule) =>
        this.matchesLevelFilter(rule.levels, level) &&
        this.matchesFilter(rule.categories, category) &&
        this.matchesFilter(rule.users, userId) &&
        this.matchesFilter(rule.modules, meta.module || module) &&
        this.matchesFilter(rule.screens, meta.screen || screen) &&
        this.matchesFilter(rule.sourceApps, this.sourceApp)
      );
    }

    if (!this.runtimeConfig?.enabled || this.isExpired()) return false;
    return (
      this.matchesLevelFilter(this.runtimeConfig.levels, level) &&
      this.matchesFilter(this.runtimeConfig.categories, category) &&
      this.matchesFilter(this.runtimeConfig.users, userId) &&
      this.matchesFilter(this.runtimeConfig.modules, meta.module || module) &&
      this.matchesFilter(this.runtimeConfig.screens, meta.screen || screen) &&
      this.matchesFilter(this.runtimeConfig.sourceApps, this.sourceApp)
    );
  }

  getMatchedRule(level, meta = {}) {
    const pathContext = buildPathContext(meta.screen);
    const category = inferCategory(level, meta.eventType, meta);
    const userId = meta.userId || this.userContext.userId || "anonymous";
    const activeRules = (this.runtimeConfig.rules || []).filter(
      (rule) => rule.enabled !== false && !this.isRuleExpired(rule)
    );
    if (!activeRules.length) return null;
    return (
      activeRules.find((rule) =>
        this.matchesLevelFilter(rule.levels, level) &&
        this.matchesFilter(rule.categories, category) &&
        this.matchesFilter(rule.users, userId) &&
        this.matchesFilter(rule.modules, meta.module || pathContext.module) &&
        this.matchesFilter(rule.screens, meta.screen || pathContext.screen) &&
        this.matchesFilter(rule.sourceApps, this.sourceApp)
      ) || null
    );
  }

  getEffectiveDetailConfig(level, meta = {}) {
    const matchedRule = this.getMatchedRule(level, meta);
    return {
      remoteDetailLevel: matchedRule?.remoteDetailLevel || this.runtimeConfig.remoteDetailLevel || "minimal",
      localConsoleDetailLevel:
        matchedRule?.localConsoleDetailLevel || this.runtimeConfig.localConsoleDetailLevel || "minimal",
      maxPayloadBytes:
        matchedRule?.maxPayloadBytes || this.runtimeConfig.maxPayloadBytes || DEFAULT_REMOTE_CONFIG.maxPayloadBytes,
      matchedRuleId: matchedRule?.id || null,
    };
  }

  recordDrop(reason, count = 1) {
    this.droppedCounters[reason] = (this.droppedCounters[reason] || 0) + count;
  }

  enforceRateLimit(level) {
    const bucket = Math.floor(Date.now() / 60000);
    if (bucket !== this.minuteWindow.bucket) {
      this.minuteWindow = { bucket, count: 0 };
    }
    this.minuteWindow.count += 1;
    if (this.minuteWindow.count <= this.runtimeConfig.rateLimitPerMinute) return false;
    if (level === "error") return false;
    this.recordDrop("rate_limited");
    return true;
  }

  makeQueueRoom(incomingLevel) {
    if (this.queue.length < this.runtimeConfig.maxQueueEvents) return true;
    const removableIndex = this.queue.findIndex(
      (item) => LEVEL_PRIORITY[item.level] < LEVEL_PRIORITY[incomingLevel]
    );
    if (removableIndex >= 0) {
      this.queue.splice(removableIndex, 1);
      this.recordDrop("queue_shed");
      return true;
    }
    this.recordDrop("queue_full");
    return false;
  }

  getCurrentNetworkContext() {
    const base = readConnectionContext();
    const quality = classifyNetworkQuality({
      ...base,
      ...(this.lastNetworkSnapshot || {}),
    });
    return {
      ...base,
      qualityStatus: this.lastNetworkSnapshot?.status || quality.status,
      qualityReasons: this.lastNetworkSnapshot?.reasons || quality.reasons,
      pingMs: this.lastNetworkSnapshot?.pingMs ?? null,
      capturedAt: this.lastNetworkSnapshot?.capturedAt || null,
      measurementSource: this.lastNetworkSnapshot?.measurementSource || "passive",
    };
  }

  buildNetworkSnapshot(reason, probe = {}) {
    const base = readConnectionContext();
    const quality = classifyNetworkQuality({ ...base, ...probe });
    return {
      ...base,
      ...probe,
      reason,
      status: quality.status,
      reasons: quality.reasons,
      capturedAt: nowIso(),
      measurementSource: probe.measurementSource || "passive",
    };
  }

  async probeNetworkLatency(force = false) {
    if (!isBrowser()) return this.lastNetworkSnapshot;
    const minGapMs = this.runtimeConfig.networkSnapshotMinGapMs || DEFAULT_REMOTE_CONFIG.networkSnapshotMinGapMs;
    if (!force && this.lastNetworkSnapshot && (Date.now() - this.lastNetworkProbeAt) < minGapMs) {
      return this.lastNetworkSnapshot;
    }
    if (this.networkProbeInFlight) return this.networkProbeInFlight;

    this.networkProbeInFlight = (async () => {
      let pingMs = null;
      try {
        const controller = typeof AbortController !== "undefined" ? new AbortController() : null;
        const timeoutId = controller
          ? window.setTimeout(() => controller.abort(), 4000)
          : null;
        const startedAt = typeof performance !== "undefined" ? performance.now() : Date.now();
        const url = `${window.location.origin}/favicon.ico?userTracerProbe=${Date.now()}`;
        await fetch(url, {
          method: "HEAD",
          cache: "no-store",
          credentials: "same-origin",
          signal: controller?.signal,
        });
        const endedAt = typeof performance !== "undefined" ? performance.now() : Date.now();
        pingMs = Math.max(0, Math.round(endedAt - startedAt));
        if (timeoutId) window.clearTimeout(timeoutId);
      } catch (error) {
        pingMs = null;
      }

      const snapshot = this.buildNetworkSnapshot("active-probe", {
        pingMs,
        measurementSource: "active-probe",
      });
      this.lastNetworkSnapshot = snapshot;
      this.lastNetworkProbeAt = Date.now();
      this.networkProbeInFlight = null;
      return snapshot;
    })();

    return this.networkProbeInFlight;
  }

  async captureNetworkSnapshot(reason, options = {}) {
    if (!isBrowser() || this.isLocalhost()) return null;
    const force = options.force === true;
    const snapshot = await this.probeNetworkLatency(force).catch(() =>
      this.buildNetworkSnapshot(reason, { measurementSource: "passive" })
    );
    const merged = {
      ...snapshot,
      reason,
      capturedAt: nowIso(),
    };
    const fingerprint = [
      merged.status,
      merged.effectiveType,
      merged.downlinkMbps,
      merged.rttMs,
      merged.pingMs,
      merged.online,
    ].join("|");
    this.lastNetworkSnapshot = merged;
    if (options.silent) return merged;
    if (fingerprint === this.lastNetworkFingerprint && !force) return merged;
    this.lastNetworkFingerprint = fingerprint;

    const level = merged.status === "poor" || merged.status === "offline" ? "warn" : "info";
    this.trace(level, "network-snapshot", `Network snapshot ${merged.status}`, {
      category: "NETWORK",
      module: "app",
      screen: isBrowser() ? window.location.pathname : "/",
      data: {
        reason,
        snapshot: merged,
      },
    });
    return merged;
  }

  buildEvent(level, eventType, message, meta = {}) {
    const pathContext = buildPathContext(meta.screen);
    const authSessionError = meta?.authSessionError ?? isAuthSessionMessage(message);
    const category = inferCategory(level, eventType, { ...meta, authSessionError });
    const traceId = meta.traceId || meta.correlationId || newId();
    const issueKey = hashString(
      [
        this.sourceApp,
        level,
        meta.module || pathContext.module,
        meta.screen || pathContext.screen,
        eventType,
        normalizeForIssueKey(message),
      ].join("|")
    );
    const detailConfig = this.getEffectiveDetailConfig(level, {
      ...meta,
      eventType,
    });
    const payload = sanitizeValue({
      ...meta.data,
      metric: meta.metric,
      error: meta.error,
      durationMs: meta.durationMs,
      droppedCounters: meta.droppedCounters,
      authSessionError,
      networkSnapshot: this.lastNetworkSnapshot,
    }, {
      detailLevel: detailConfig.remoteDetailLevel,
    });

    return {
      sourceApp: this.sourceApp,
      instanceBackend: this.instanceBackend,
      instanceRegion: this.instanceRegion,
      userId: meta.userId || this.userContext.userId || "anonymous",
      sessionId: this.sessionId,
      traceId,
      correlationId: meta.correlationId || traceId,
      module: meta.module || pathContext.module,
      screen: meta.screen || pathContext.screen,
      eventType,
      category,
      level,
      issueKey,
      remoteDetailLevel: detailConfig.remoteDetailLevel,
      localConsoleDetailLevel: detailConfig.localConsoleDetailLevel,
      ts: nowIso(),
      message: truncateString(message, 1200),
      browser: buildBrowserContext(),
      network: this.getCurrentNetworkContext(),
      payload,
      repeatCount: 1,
      authSessionError,
      matchedRuleId: detailConfig.matchedRuleId,
      maxPayloadBytes: detailConfig.maxPayloadBytes,
      dedupeWindowMs: authSessionError
        ? this.runtimeConfig.sessionErrorDedupeWindowMs
        : this.runtimeConfig.dedupeWindowMs,
    };
  }

  shouldWriteLocalConsole(event) {
    if (this.isLocalhost()) return false;
    if (event.localConsoleDetailLevel && event.localConsoleDetailLevel !== "minimal") return true;
    return (event.level === "error" && this.runtimeConfig?.alwaysShipErrors !== false) || event.authSessionError;
  }

  writeLocalConsole(event, meta = {}) {
    const consoleTarget =
      this.originalConsole?.[event.level] ||
      this.originalConsole?.error ||
      this.originalConsole?.log;
    if (typeof consoleTarget !== "function") return;

    const summary = event.authSessionError
      ? "Session issue"
      : event.level === "error"
        ? "UI error"
        : "User trace";

    const details = {
      reference: event.traceId || event.issueKey,
      ts: event.ts,
      app: event.sourceApp,
      instanceBackend: event.instanceBackend,
      instanceRegion: event.instanceRegion,
      module: event.module,
      screen: event.screen,
      eventType: event.eventType,
      level: event.level,
      category: event.category,
      user: maskIdentifier(event.userId),
      session: event.sessionId ? String(event.sessionId).slice(-8) : null,
      issueKey: event.issueKey,
      message: truncateString(event.message, 240),
    };

    const consoleDetailLevel =
      event.localConsoleDetailLevel || this.runtimeConfig.localConsoleDetailLevel || "minimal";
    if (consoleDetailLevel !== "minimal") {
      details.browser = sanitizeValue(event.browser, { detailLevel: consoleDetailLevel });
      details.network = sanitizeValue(event.network, { detailLevel: consoleDetailLevel });
    }
    if (consoleDetailLevel === "full") {
      details.payload = sanitizeValue(event.payload, { detailLevel: "enhanced", maxString: 1200 });
    }

    const detailText = [
      details.message || "",
      details.screen ? `screen=${details.screen}` : "",
      details.module ? `module=${details.module}` : "",
      details.instanceBackend ? `backend=${details.instanceBackend}` : "",
      details.reference ? `ref=${details.reference}` : "",
      details.issueKey ? `issue=${details.issueKey}` : "",
      details.user ? `user=${details.user}` : "",
    ].filter(Boolean).join(" | ");

    consoleTarget(`${summary}: ${detailText}`);
  }

  enqueueEvent(event) {
    if (this.enforceRateLimit(event.level)) return;
    if (!this.makeQueueRoom(event.level)) return;

    const dedupeFingerprint = hashString(`${event.userId}|${event.issueKey}`);
    const existing = this.dedupeMap.get(dedupeFingerprint);
    const nowMs = Date.now();
    if (existing && existing.expiresAt > nowMs) {
      existing.event.repeatCount += 1;
      existing.event.ts = event.ts;
      return;
    }

    this.queue.push(event);
    this.dedupeMap.set(dedupeFingerprint, {
      event,
      expiresAt: nowMs + (event.dedupeWindowMs || this.runtimeConfig.dedupeWindowMs),
    });
    this.scheduleFlush();
  }

  scheduleFlush() {
    if (this.flushTimer || !isBrowser()) return;
    const delay = this.queue.length >= this.runtimeConfig.maxBatchEvents ? 0 : this.runtimeConfig.flushIntervalMs;
    this.flushTimer = window.setTimeout(() => {
      this.flushTimer = null;
      this.flush();
    }, delay);
  }

  async flush() {
    const canShip = !this.isLocalhost() && (
      this.runtimeConfig?.enabled ||
      this.runtimeConfig?.alwaysShipErrors ||
      (this.runtimeConfig?.rules || []).length
    );
    if (this.flushInFlight || !canShip) {
      this.queue = [];
      return;
    }
    if (!this.queue.length) return;

    this.flushInFlight = true;
    const batch = [];
    let batchBytes = 0;
    while (this.queue.length && batch.length < this.runtimeConfig.maxBatchEvents) {
      const candidate = this.queue[0];
      const candidateBytes = new Blob([safeJsonStringify(candidate)]).size;
      const candidateMaxBytes = Math.min(
        this.runtimeConfig.maxEventBytes,
        candidate.maxPayloadBytes || this.runtimeConfig.maxEventBytes
      );
      if (candidateBytes > candidateMaxBytes) {
        this.queue.shift();
        this.recordDrop("event_too_large");
        continue;
      }
      if (batch.length && batchBytes + candidateBytes > this.runtimeConfig.maxBatchBytes) {
        break;
      }
      batch.push(this.queue.shift());
      batchBytes += candidateBytes;
    }

    const droppedCounters = Object.keys(this.droppedCounters).length ? { ...this.droppedCounters } : undefined;
    this.droppedCounters = {};

    const envelope = {
      sourceApp: this.sourceApp,
      instanceBackend: this.instanceBackend,
      instanceRegion: this.instanceRegion,
      sessionId: this.sessionId,
      userId: this.userContext.userId || "anonymous",
      sentAt: nowIso(),
      browser: buildBrowserContext(),
      network: this.getCurrentNetworkContext(),
      droppedCounters,
      events: batch,
    };

    try {
      await this.shipBatch(envelope);
    } catch (error) {
      this.recordDrop("ship_failed", batch.length);
    } finally {
      this.flushInFlight = false;
      if (this.queue.length) this.scheduleFlush();
    }
  }

  trace(level, eventType, message, meta = {}) {
    if (!this.shouldTrace(level, { ...meta, eventType, message })) return;
    const event = this.buildEvent(level, eventType, message, meta);
    if (this.shouldWriteLocalConsole(event)) {
      this.writeLocalConsole(event, meta);
    }
    this.enqueueEvent(event);
  }

  captureRoute(pathname) {
    const path = pathname || (isBrowser() ? window.location.pathname : "/");
    const pathContext = buildPathContext(path);
    this.captureNetworkSnapshot("navigation", { silent: true });
    this.trace("info", "navigation", `Navigated to ${path}`, {
      category: "UI_ACTIVITY",
      module: pathContext.module,
      screen: path,
    });
  }

  captureWebVital(metric) {
    if (!metric) return;
    this.trace("info", "performance", `Web vital ${metric.name}`, {
      category: "PERFORMANCE",
      traceId: metric.id,
      metric: sanitizeValue(metric),
      module: "app",
      screen: isBrowser() ? window.location.pathname : "/",
    });
  }

  startOperation(name, meta = {}) {
    const startedAt = typeof performance !== "undefined" ? performance.now() : Date.now();
    const traceId = meta.traceId || newId();
    return {
      traceId,
      finish: (extra = {}) => {
        const endedAt = typeof performance !== "undefined" ? performance.now() : Date.now();
        const durationMs = Math.round(endedAt - startedAt);
        const level =
          durationMs >= this.runtimeConfig.slowOperationWarnMs
            ? "warn"
            : "info";
        const networkSnapshot = this.lastNetworkSnapshot;
        if (durationMs >= this.runtimeConfig.slowOperationInfoMs) {
          this.captureNetworkSnapshot("slow-operation", {
            silent: durationMs < this.runtimeConfig.slowOperationWarnMs,
            force: durationMs >= this.runtimeConfig.slowOperationWarnMs,
          });
        }
        this.trace(level, "performance", `${name} completed`, {
          ...meta,
          ...extra,
          traceId,
          category: "PERFORMANCE",
          durationMs,
          data: {
            ...(meta.data || {}),
            ...(extra.data || {}),
            operationName: name,
            impactKind: extra.impactKind || meta.impactKind || null,
            impactReason: extra.impactReason || meta.impactReason || null,
            slowOperation: durationMs >= this.runtimeConfig.slowOperationInfoMs,
            networkQuality: networkSnapshot?.status || null,
            networkReasons: networkSnapshot?.reasons || [],
          },
        });
      },
      fail: (error, extra = {}) => {
        const endedAt = typeof performance !== "undefined" ? performance.now() : Date.now();
        const durationMs = Math.round(endedAt - startedAt);
        this.captureNetworkSnapshot("failed-operation", { silent: true, force: true });
        this.trace("error", "performance", `${name} failed`, {
          ...meta,
          ...extra,
          traceId,
          category: "PERFORMANCE",
          durationMs,
          error,
          data: {
            ...(meta.data || {}),
            ...(extra.data || {}),
            operationName: name,
            impactKind: extra.impactKind || meta.impactKind || null,
            impactReason: extra.impactReason || meta.impactReason || null,
            networkQuality: this.lastNetworkSnapshot?.status || null,
            networkReasons: this.lastNetworkSnapshot?.reasons || [],
          },
        });
      },
    };
  }
}
